import { createPortal } from "react-dom";
import { LoaderCircle, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import {
  apiClient,
  type KeyMessage,
  type LinkMentionsResult,
  type LinkTarget,
  type PostSource,
  type Spokesperson,
  type Theme
} from "@/shared/api/api-client";
import { errorMessage } from "@/shared/api/http";
import { usePolarityChoices } from "@/shared/components/polarity-choices";
import { useDismiss } from "@/shared/hooks/useDismiss";
import { APPROACH_CHOICES } from "@/features/mentions/mention-filter-criteria";
import { CatalogMultiSelect } from "@/features/mentions/MentionClassificationEditor";
import {
  buildLinkTargets,
  emptyTab,
  linkResultMessage,
  type LinkTab
} from "@/features/mentions/link-mention";
import { Select } from "@/shared/components/Select";

/**
 * Vincular uma ou várias menções a OUTRAS marcas, à mão (2026-10-03) —
 * a outra metade do descarte: a esteira não casou a publicação com a
 * marca, e alguém a vincula daqui.
 *
 * Um pop-up só pra uma e pra várias (decisão da dona): a pessoa marca
 * as marcas de destino, e cada marca marcada vira uma ABA com a
 * classificação daquela marca. Com várias publicações selecionadas, o
 * que está na aba vale pra todas. Aba em branco cria a menção sem
 * classificação — nenhuma IA é chamada.
 *
 * Concorrente também é destino, e a aba dele oferece o catálogo DELE:
 * temas, mensagens-chave e porta-vozes do concorrente são cadastro
 * próprio (Ajustes, "Cadastro de: Concorrente").
 *
 * Mensagem-chave é cadastro por FONTE (2026-11-04, decisão da dona): a
 * aba só oferece as da fonte das publicações, e com imprensa e redes
 * juntas o campo some com o aviso (o servidor recusa o mesmo caso).
 * Quem diz as fontes é `loadSources`, porque a seleção atravessa
 * páginas e a tela não tem todas as menções em mãos.
 *
 * "Mover" é a caixinha do rodapé: vincula e descarta a menção de
 * origem. Desmarcada é só vincular; o descarte sozinho continua no menu
 * da linha.
 */

type Catalog = {
  themes: Theme[];
  /** id do grupo -> nome (título dos temas na caixa de escolha). */
  themeGroupNames: Map<number, string>;
  keyMessages: KeyMessage[];
  spokespersons: Spokesperson[];
};

export function LinkMentionDialog({
  token,
  mentionIds,
  sourceBrandId,
  loadSources,
  onDone,
  onClose
}: {
  token: string;
  mentionIds: number[];
  /** A marca da menção de origem, quando é UMA só — sai da lista de
   *  destinos, porque vincular à própria marca não faz nada. */
  sourceBrandId?: number;
  /** As fontes (`press` | `network`) das menções de origem. */
  loadSources: () => Promise<PostSource[]>;
  /** Depois de vincular: a mensagem pronta pro aviso da tela. */
  onDone: (message: string, result: LinkMentionsResult) => void;
  onClose: () => void;
}) {
  const polarityChoices = usePolarityChoices();
  const [targets, setTargets] = useState<LinkTarget[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  // As abas, na ordem em que as marcas foram marcadas.
  const [tabs, setTabs] = useState<LinkTab[]>([]);
  const [active, setActive] = useState<number | null>(null);
  const [catalogs, setCatalogs] = useState<Record<number, Catalog>>({});
  const requested = useRef(new Set<number>());
  const [discardSource, setDiscardSource] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useDismiss(true, () => {
    if (!saving) onClose();
  });

  // `null` = ainda conferindo. Uma vez, ao abrir: as menções de origem
  // não mudam com o diálogo aberto, e a função do pai é recriada a cada
  // render dele.
  const [sources, setSources] = useState<PostSource[] | null>(null);
  const [sourcesError, setSourcesError] = useState<string | null>(null);
  const loadSourcesRef = useRef(loadSources);
  useEffect(() => {
    let alive = true;
    loadSourcesRef
      .current()
      .then(found => {
        if (alive) setSources(found);
      })
      .catch(failure => {
        if (!alive) return;
        setSourcesError(
          errorMessage(failure, "Não foi possível conferir a fonte das publicações.")
        );
      });
    return () => {
      alive = false;
    };
  }, []);
  const singleSource = sources?.length === 1 ? sources[0] : null;
  const keyMessagesNote =
    sourcesError ??
    (sources === null
      ? "Conferindo a fonte das publicações…"
      : singleSource === null
        ? "As publicações escolhidas são de imprensa e de redes: mensagens-chave são cadastradas por fonte e só se vinculam quando todas são da mesma. Vincule sem mensagem-chave ou separe por fonte."
        : null);

  useEffect(() => {
    let alive = true;
    apiClient
      .linkTargets(token)
      .then(found => {
        if (alive) setTargets(found.filter(target => target.brand_id !== sourceBrandId));
      })
      .catch(failure => {
        if (!alive) return;
        setTargets([]);
        setLoadError(errorMessage(failure, "Não foi possível carregar as marcas."));
      });
    return () => {
      alive = false;
    };
  }, [token, sourceBrandId]);

  const targetOf = (brandId: number) => targets?.find(item => item.brand_id === brandId);

  // O catálogo da marca, buscado na primeira vez que a aba dela
  // existe. Marca de concorrente lê o catálogo DELE (cadastro próprio,
  // feito em Ajustes com "Cadastro de: Concorrente").
  const loadCatalog = (brandId: number, competitorId: number | null) => {
    if (requested.current.has(brandId)) return;
    requested.current.add(brandId);
    const api = apiClient.onCompetitor(competitorId);
    void Promise.all([
      api.listThemes(token, { brandId }),
      api.listKeyMessages(token, { brandId }),
      api.listSpokespersons(token, { brandId }),
      // O grupo é só o título: falhar não derruba o catálogo.
      api.listThemeGroups(token).catch(() => [])
    ])
      .then(([themes, keyMessages, spokespersons, groups]) =>
        setCatalogs(current => ({
          ...current,
          [brandId]: {
            themes,
            keyMessages,
            spokespersons,
            themeGroupNames: new Map(groups.map(group => [group.id, group.name]))
          }
        }))
      )
      .catch(() => {
        // Sem catálogo a aba fica só com polaridade e abordagem — dá pra
        // vincular e completar depois no editor da menção.
        setCatalogs(current => ({
          ...current,
          [brandId]: {
            themes: [],
            keyMessages: [],
            spokespersons: [],
            themeGroupNames: new Map()
          }
        }));
      });
  };

  const toggleTarget = (target: LinkTarget) => {
    const exists = tabs.some(tab => tab.brandId === target.brand_id);
    if (exists) {
      const remaining = tabs.filter(tab => tab.brandId !== target.brand_id);
      setTabs(remaining);
      if (active === target.brand_id) setActive(remaining[0]?.brandId ?? null);
      return;
    }
    setTabs(current => [...current, emptyTab(target.brand_id)]);
    setActive(target.brand_id);
    loadCatalog(target.brand_id, target.competitor_id);
  };

  const patchTab = (brandId: number, patch: Partial<LinkTab>) =>
    setTabs(current =>
      current.map(tab => (tab.brandId === brandId ? { ...tab, ...patch } : tab))
    );

  const toggleId = (brandId: number, field: "themeIds" | "keyMessageIds" | "spokespersonIds", id: number) =>
    setTabs(current =>
      current.map(tab =>
        tab.brandId === brandId
          ? {
              ...tab,
              [field]: tab[field].includes(id)
                ? tab[field].filter(value => value !== id)
                : [...tab[field], id]
            }
          : tab
      )
    );

  const submit = async () => {
    setSaving(true);
    setError(null);
    try {
      const result = await apiClient.linkMentions(token, {
        mentionIds,
        targets: buildLinkTargets(tabs),
        discardSource
      });
      onDone(linkResultMessage(result), result);
    } catch (failure) {
      setError(errorMessage(failure, "Não foi possível vincular."));
      setSaving(false);
    }
  };

  const own = (targets ?? []).filter(target => target.competitor_name === null);
  const rivals = (targets ?? []).filter(target => target.competitor_name !== null);
  const current = tabs.find(tab => tab.brandId === active) ?? null;
  const currentTarget = current ? targetOf(current.brandId) : undefined;
  const catalog = current ? catalogs[current.brandId] : undefined;
  const count = mentionIds.length;

  const targetGroup = (title: string, items: LinkTarget[]) =>
    items.length > 0 && (
      <div className="link-target-group">
        <span>{title}</span>
        <div className="brand-picker">
          {items.map(target => (
            <label key={target.brand_id} className="brand-picker-item">
              <input
                type="checkbox"
                checked={tabs.some(tab => tab.brandId === target.brand_id)}
                onChange={() => toggleTarget(target)}
              />
              {target.competitor_name !== null &&
              target.competitor_name !== target.brand_name
                ? `${target.competitor_name} · ${target.brand_name}`
                : target.brand_name}
            </label>
          ))}
        </div>
      </div>
    );

  return createPortal(
    <div className="modal-overlay" onClick={saving ? undefined : onClose}>
      <div
        className="modal-container bulk-dialog link-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="link-dialog-title"
        onClick={event => event.stopPropagation()}
      >
        <header className="modal-header">
          <div>
            <span className="eyebrow">VINCULAR A OUTRA MARCA</span>
            <h2 id="link-dialog-title">
              {count === 1
                ? "Vincular esta publicação"
                : `Vincular ${count.toLocaleString("pt-BR")} publicações`}
            </h2>
            <p className="bulk-dialog-target">
              Cria a menção nas marcas escolhidas, sem passar pela IA. A menção
              de origem continua como está.
            </p>
          </div>
          <button
            className="icon-button"
            type="button"
            onClick={onClose}
            aria-label="Fechar"
            disabled={saving}
          >
            <X size={18} />
          </button>
        </header>
        <div className="modal-body">
          <div className="classification-editor">
            {targets === null && (
              <p className="mention-detail-note">
                <LoaderCircle className="spin" size={14} /> Carregando as marcas…
              </p>
            )}
            {loadError && <div className="login-error">{loadError}</div>}
            {targets !== null && targets.length === 0 && !loadError && (
              <p className="mention-detail-note">
                Não há outra marca nem concorrente cadastrado para vincular.
              </p>
            )}
            {targetGroup("Marcas da empresa", own)}
            {targetGroup("Concorrentes", rivals)}

            {tabs.length > 0 && (
              <>
                <div className="filter-chip-group link-tabs" role="tablist">
                  {tabs.map(tab => (
                    <button
                      key={tab.brandId}
                      type="button"
                      role="tab"
                      aria-selected={tab.brandId === active}
                      className={`filter-chip ${tab.brandId === active ? "active" : ""}`}
                      onClick={() => setActive(tab.brandId)}
                    >
                      {targetOf(tab.brandId)?.brand_name ?? `Marca ${tab.brandId}`}
                    </button>
                  ))}
                </div>
                {current && (
                  <div className="link-tab-panel" role="tabpanel">
                    <div className="classification-editor-field">
                      <span>Polaridade</span>
                      <Select
                        value={current.polarity ?? ""}
                        onChange={event =>
                          patchTab(current.brandId, { polarity: event.target.value || null })
                        }
                      >
                        <option value="">Sem classificação</option>
                        {polarityChoices.map(option => (
                          <option key={option.name} value={option.name}>
                            {option.label}
                          </option>
                        ))}
                      </Select>
                    </div>
                    <div className="classification-editor-field">
                      <span>Abordagem</span>
                      <Select
                        value={current.approach ?? ""}
                        onChange={event =>
                          patchTab(current.brandId, { approach: event.target.value || null })
                        }
                      >
                        <option value="">Sem classificação</option>
                        {APPROACH_CHOICES.map(option => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </Select>
                    </div>
                    {catalog === undefined ? (
                      <p className="mention-detail-note">
                        <LoaderCircle className="spin" size={14} /> Carregando o
                        catálogo da marca…
                      </p>
                    ) : (
                      <>
                        <CatalogMultiSelect
                          label="Subtemas"
                          options={catalog.themes.map(t => ({
                            id: t.id,
                            name: t.name,
                            group:
                              t.theme_group_id == null
                                ? null
                                : (catalog.themeGroupNames.get(t.theme_group_id) ?? null)
                          }))}
                          selected={current.themeIds}
                          onToggle={id => toggleId(current.brandId, "themeIds", id)}
                          onClear={() => patchTab(current.brandId, { themeIds: [] })}
                        />
                        {keyMessagesNote === null ? (
                          <CatalogMultiSelect
                            label="Mensagens-chave"
                            options={catalog.keyMessages
                              .filter(k => k.data_source === singleSource)
                              .map(k => ({ id: k.id, name: k.message }))}
                            selected={current.keyMessageIds}
                            onToggle={id => toggleId(current.brandId, "keyMessageIds", id)}
                            onClear={() => patchTab(current.brandId, { keyMessageIds: [] })}
                          />
                        ) : (
                          <p className="mention-detail-note" role="status">
                            {keyMessagesNote}
                          </p>
                        )}
                        <CatalogMultiSelect
                          label="Porta-vozes"
                          options={catalog.spokespersons.map(s => ({ id: s.id, name: s.name }))}
                          selected={current.spokespersonIds}
                          onToggle={id => toggleId(current.brandId, "spokespersonIds", id)}
                          onClear={() => patchTab(current.brandId, { spokespersonIds: [] })}
                        />
                        {currentTarget?.competitor_name != null &&
                          catalog.themes.length +
                            catalog.keyMessages.length +
                            catalog.spokespersons.length ===
                            0 && (
                            <p className="mention-detail-note">
                              Este concorrente ainda não tem subtemas, mensagens-chave nem
                              porta-vozes cadastrados. Cadastre em Ajustes, escolhendo
                              "Cadastro de: Concorrente".
                            </p>
                          )}
                      </>
                    )}
                    {count > 1 && (
                      <p className="mention-detail-note">
                        O que está nesta aba vale para as {count.toLocaleString("pt-BR")}{" "}
                        publicações selecionadas.
                      </p>
                    )}
                  </div>
                )}
              </>
            )}

            <label className="filter-check">
              <input
                type="checkbox"
                checked={discardSource}
                onChange={event => setDiscardSource(event.target.checked)}
              />
              <span>
                Mover: descartar {count === 1 ? "a menção de origem" : "as menções de origem"}{" "}
                depois de vincular
              </span>
            </label>
            <p className="mention-detail-note">
              Publicação que já tem menção na marca escolhida não é alterada. A nota
              reaproveita o que é da publicação (formato, veículo, canal) e recalcula
              o que depende da classificação.
            </p>
            {error && (
              <div className="account-feedback error" aria-live="polite">
                {error}
              </div>
            )}
          </div>
        </div>
        <footer className="modal-footer">
          <button
            type="button"
            className="secondary-button"
            onClick={onClose}
            disabled={saving}
          >
            Cancelar
          </button>
          <button
            type="button"
            className="primary-button"
            onClick={() => void submit()}
            disabled={saving || tabs.length === 0}
          >
            {saving ? (
              <LoaderCircle className="spin" size={16} />
            ) : tabs.length === 0 ? (
              "Escolha a marca"
            ) : discardSource ? (
              `Mover para ${tabs.length} ${tabs.length === 1 ? "marca" : "marcas"}`
            ) : (
              `Vincular a ${tabs.length} ${tabs.length === 1 ? "marca" : "marcas"}`
            )}
          </button>
        </footer>
      </div>
    </div>,
    document.body
  );
}
